import UAParser from 'ua-parser-js';
import * as Sentry from '@sentry/react';
import env from '../utils/env';

// Types for user activity tracking
export interface DeviceInfo {
  deviceType: 'mobile' | 'tablet' | 'desktop' | 'unknown';
  os: string;
  osVersion: string;
  browser: string;
  browserVersion: string;
  screenResolution: string;
  userAgent: string;
  isMobile: boolean;
  isTablet: boolean;
  isDesktop: boolean;
}

export interface UserSession {
  sessionId: string;
  userId?: string;
  userRole?: string;
  startTime: Date;
  lastActivity: Date;
  pageViews: number;
  device: DeviceInfo;
  ipAddress?: string;
}

export interface ActivityEvent {
  eventType: 'page_view' | 'user_action' | 'api_call' | 'auth_event' | 'error_event';
  page?: string;
  action?: string;
  component?: string;
  apiEndpoint?: string;
  method?: string;
  statusCode?: number;
  errorMessage?: string;
  userId?: string;
  userRole?: string;
  sessionId: string;
  timestamp: Date;
  device: DeviceInfo;
  metadata?: Record<string, any>;
}

export interface AuditLog {
  eventId: string;
  userId?: string;
  userRole?: string;
  action: string;
  resource: string;
  outcome: 'success' | 'failure' | 'warning';
  ipAddress?: string;
  userAgent: string;
  timestamp: Date;
  metadata?: Record<string, any>;
}

class UserActivityService {
  private sessionId: string;
  private currentSession: UserSession | null = null;
  private deviceInfo: DeviceInfo;
  private elkEndpoint: string;
  private elkApiKey: string;
  private isTrackingEnabled: boolean;
  private activityQueue: ActivityEvent[] = [];
  private auditQueue: AuditLog[] = [];

  constructor() {
    this.sessionId = this.generateSessionId();
    this.deviceInfo = this.detectDevice();
            this.elkEndpoint = env.logEndpoint || '';
        this.elkApiKey = env.logApiKey || '';
        this.isTrackingEnabled = env.enableUserTracking;
    
    if (this.isTrackingEnabled) {
      this.initializeSession();
      this.setupEventListeners();
      this.startPeriodicFlush();
    }
  }

  // Generate unique session ID
  private generateSessionId(): string {
    return 'sess_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
  }

  // Detect device information using UAParser
  private detectDevice(): DeviceInfo {
    const parser = new UAParser();
    const result = parser.getResult();

    const screenResolution = `${window.screen.width}x${window.screen.height}`;
    const deviceType = result.device.type || 'desktop';
    
    return {
      deviceType: this.mapDeviceType(deviceType),
      os: result.os.name || 'Unknown',
      osVersion: result.os.version || 'Unknown',
      browser: result.browser.name || 'Unknown',
      browserVersion: result.browser.version || 'Unknown',
      screenResolution,
      userAgent: navigator.userAgent,
      isMobile: deviceType === 'mobile',
      isTablet: deviceType === 'tablet',
      isDesktop: deviceType !== 'mobile' && deviceType !== 'tablet',
    };
  }

  private mapDeviceType(type: string): 'mobile' | 'tablet' | 'desktop' | 'unknown' {
    switch (type) {
      case 'mobile':
        return 'mobile';
      case 'tablet':
        return 'tablet';
      case 'desktop':
      case undefined:
        return 'desktop';
      default:
        return 'unknown';
    }
  }

  // Initialize user session
  private initializeSession(): void {
    this.currentSession = {
      sessionId: this.sessionId,
      startTime: new Date(),
      lastActivity: new Date(),
      pageViews: 0,
      device: this.deviceInfo,
    };

    this.trackEvent({
      eventType: 'page_view',
      page: 'session_start',
      sessionId: this.sessionId,
      timestamp: new Date(),
      device: this.deviceInfo,
    });
  }

  // Set user information when user logs in
  public setUser(userId: string, userRole: string): void {
    if (this.currentSession) {
      this.currentSession.userId = userId;
      this.currentSession.userRole = userRole;
    }

    // Set Sentry user context
    Sentry.setUser({
      id: userId,
      role: userRole,
    });

    this.trackAuditEvent({
      action: 'user_login',
      resource: 'authentication',
      outcome: 'success',
      userId,
      userRole,
      metadata: {
        sessionId: this.sessionId,
        device: this.deviceInfo.deviceType,
        browser: this.deviceInfo.browser,
      },
    });
  }

  // Clear user information when user logs out
  public clearUser(): void {
    const userId = this.currentSession?.userId;
    const userRole = this.currentSession?.userRole;

    if (this.currentSession) {
      this.currentSession.userId = undefined;
      this.currentSession.userRole = undefined;
    }

    // Clear Sentry user context
    Sentry.setUser(null);

    if (userId) {
      this.trackAuditEvent({
        action: 'user_logout',
        resource: 'authentication',
        outcome: 'success',
        userId,
        userRole,
        metadata: {
          sessionId: this.sessionId,
        },
      });
    }
  }

  // Track page views
  public trackPageView(page: string, metadata?: Record<string, any>): void {
    if (!this.isTrackingEnabled) return;

    if (this.currentSession) {
      this.currentSession.pageViews++;
      this.currentSession.lastActivity = new Date();
    }

    this.trackEvent({
      eventType: 'page_view',
      page,
      sessionId: this.sessionId,
      timestamp: new Date(),
      device: this.deviceInfo,
      userId: this.currentSession?.userId,
      userRole: this.currentSession?.userRole,
      metadata,
    });

    // Track in Sentry for performance monitoring
    Sentry.addBreadcrumb({
      message: `Page view: ${page}`,
      category: 'navigation',
      level: 'info',
      data: { page, deviceType: this.deviceInfo.deviceType },
    });
  }

  // Track user actions (button clicks, form submissions, etc.)
  public trackUserAction(action: string, component: string, metadata?: Record<string, any>): void {
    if (!this.isTrackingEnabled) return;

    this.trackEvent({
      eventType: 'user_action',
      action,
      component,
      sessionId: this.sessionId,
      timestamp: new Date(),
      device: this.deviceInfo,
      userId: this.currentSession?.userId,
      userRole: this.currentSession?.userRole,
      metadata,
    });

    // Track in Sentry
    Sentry.addBreadcrumb({
      message: `User action: ${action}`,
      category: 'user',
      level: 'info',
      data: { action, component, ...metadata },
    });
  }

  // Track API calls
  public trackApiCall(
    endpoint: string,
    method: string,
    statusCode: number,
    duration?: number,
    metadata?: Record<string, any>
  ): void {
    if (!this.isTrackingEnabled) return;

    this.trackEvent({
      eventType: 'api_call',
      apiEndpoint: endpoint,
      method,
      statusCode,
      sessionId: this.sessionId,
      timestamp: new Date(),
      device: this.deviceInfo,
      userId: this.currentSession?.userId,
      userRole: this.currentSession?.userRole,
      metadata: { duration, ...metadata },
    });

    // Track performance in Sentry
    if (duration) {
      Sentry.addBreadcrumb({
        message: `API call: ${method} ${endpoint}`,
        category: 'http',
        level: statusCode >= 400 ? 'error' : 'info',
        data: { endpoint, method, statusCode, duration },
      });
    }
  }

  // Track errors
  public trackError(error: Error, context?: string, metadata?: Record<string, any>): void {
    this.trackEvent({
      eventType: 'error_event',
      errorMessage: error.message,
      sessionId: this.sessionId,
      timestamp: new Date(),
      device: this.deviceInfo,
      userId: this.currentSession?.userId,
      userRole: this.currentSession?.userRole,
      metadata: { context, stack: error.stack, ...metadata },
    });

    // Send to Sentry
    Sentry.captureException(error, {
      contexts: {
        userActivity: {
          sessionId: this.sessionId,
          context,
          device: this.deviceInfo.deviceType,
        },
      },
      extra: metadata,
    });
  }

  // Track audit events for security and compliance
  public trackAuditEvent(auditData: Omit<AuditLog, 'eventId' | 'timestamp' | 'userAgent'>): void {
    const auditLog: AuditLog = {
      eventId: 'audit_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9),
      timestamp: new Date(),
      userAgent: this.deviceInfo.userAgent,
      ...auditData,
    };

    this.auditQueue.push(auditLog);

    // Also track as regular event for consistency
    this.trackEvent({
      eventType: 'auth_event',
      action: auditData.action,
      sessionId: this.sessionId,
      timestamp: new Date(),
      device: this.deviceInfo,
      userId: auditData.userId,
      userRole: auditData.userRole,
      metadata: auditData.metadata,
    });
  }

  // Generic event tracking
  private trackEvent(event: ActivityEvent): void {
    this.activityQueue.push(event);

    // Flush immediately for critical events
    if (event.eventType === 'error_event' || event.eventType === 'auth_event') {
      this.flushQueues();
    }
  }

  // Set up event listeners for automatic tracking
  private setupEventListeners(): void {
    // Track page visibility changes
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        this.trackUserAction('page_hidden', 'document');
        this.flushQueues();
      } else {
        this.trackUserAction('page_visible', 'document');
      }
    });

    // Track unload events
    window.addEventListener('beforeunload', () => {
      this.flushQueues();
    });

    // Track errors globally
    window.addEventListener('error', (event) => {
      this.trackError(new Error(event.message), 'global_error', {
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
      });
    });

    // Track unhandled promise rejections
    window.addEventListener('unhandledrejection', (event) => {
      this.trackError(new Error(event.reason), 'unhandled_promise_rejection');
    });
  }

  // Start periodic flush of queued events
  private startPeriodicFlush(): void {
    // Flush every 30 seconds
    setInterval(() => {
      this.flushQueues();
    }, 30000);
  }

  // Flush queued events to ELK stack
  private async flushQueues(): Promise<void> {
    if (this.activityQueue.length === 0 && this.auditQueue.length === 0) return;

    try {
      // Send activity events
      if (this.activityQueue.length > 0 && this.elkEndpoint) {
        await this.sendToELK('user-activity', this.activityQueue);
        this.activityQueue = [];
      }

      // Send audit logs
      if (this.auditQueue.length > 0 && this.elkEndpoint) {
        await this.sendToELK('audit-logs', this.auditQueue);
        this.auditQueue = [];
      }
    } catch (error) {
      console.error('Failed to flush activity logs:', error);
      // Keep events in queue for retry
    }
  }

  // Send data to ELK stack
  private async sendToELK(index: string, data: any[]): Promise<void> {
    if (!this.elkEndpoint || !this.elkApiKey) {
      console.log('ELK logging not configured, events:', data);
      return;
    }

    const response = await fetch(`${this.elkEndpoint}/${index}/_bulk`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-ndjson',
        'Authorization': `ApiKey ${this.elkApiKey}`,
      },
      body: data
        .map((item) => [
          JSON.stringify({ index: {} }),
          JSON.stringify(item),
        ])
        .flat()
        .join('\n') + '\n',
    });

    if (!response.ok) {
      throw new Error(`ELK logging failed: ${response.statusText}`);
    }
  }

  // Get current session information
  public getSession(): UserSession | null {
    return this.currentSession;
  }

  // Get device information
  public getDeviceInfo(): DeviceInfo {
    return this.deviceInfo;
  }

  // Update activity timestamp
  public updateActivity(): void {
    if (this.currentSession) {
      this.currentSession.lastActivity = new Date();
    }
  }
}

// Create singleton instance
export const userActivityService = new UserActivityService();

// Export React hook for easy access
export const useUserTracking = () => {
  const trackPageView = (page: string, metadata?: Record<string, any>) => {
    userActivityService.trackPageView(page, metadata);
  };

  const trackUserAction = (action: string, component: string, metadata?: Record<string, any>) => {
    userActivityService.trackUserAction(action, component, metadata);
  };

  const trackApiCall = (
    endpoint: string,
    method: string,
    statusCode: number,
    duration?: number,
    metadata?: Record<string, any>
  ) => {
    userActivityService.trackApiCall(endpoint, method, statusCode, duration, metadata);
  };

  const trackError = (error: Error, context?: string, metadata?: Record<string, any>) => {
    userActivityService.trackError(error, context, metadata);
  };

  return {
    trackPageView,
    trackUserAction,
    trackApiCall,
    trackError,
    deviceInfo: userActivityService.getDeviceInfo(),
    session: userActivityService.getSession(),
  };
};

export default userActivityService; 